import axios from 'axios';
import * as auth from './auth.js';

const API_BASE_URL = 'http://localhost:5000/api';

// Helper function to create authorization headers
const getAuthHeaders = () => {
  const token = localStorage.getItem(auth.TOKEN_KEY);
  return {
    Authorization: token ? `Bearer ${token}` : ''
  };
};

// Interfaces
export interface CashBalance {
  id: number;
  date: string;
  safeBalance: number;
  bankBalance: number;
  notes: string;
}

export interface Denomination {
  id: string;
  name: string;
  value: number;
  count: number;
  total: number;
}

export interface CashCount {
  id?: number;
  date: string;
  expectedAmount: number;
  actualAmount: number;
  difference: number;
  status: 'draft' | 'completed';
  notes: string;
  denominations: Denomination[];
  countedBy: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CashTransaction {
  id?: number;
  date: string;
  type: 'deposit' | 'withdraw' | 'transfer_to_bank' | 'transfer_from_bank';
  amount: number;
  reference: string;
  notes: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Get the current cash balance
export const getCurrentCashBalance = async (): Promise<CashBalance> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/cash/balance`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching cash balance:', error);
    throw error;
  }
};

// Save a cash count
export const saveCashCount = async (cashCount: CashCount): Promise<CashCount> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/cash-counts`, cashCount, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error saving cash count:', error);
    throw error;
  }
};

// Get cash count history
export const getCashCounts = async (startDate?: string, endDate?: string): Promise<CashCount[]> => {
  try {
    let url = `${API_BASE_URL}/cash-counts`;
    
    // Add date filters if provided
    if (startDate || endDate) {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      url += `?${params.toString()}`;
    }
    
    const response = await axios.get(url, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching cash counts:', error);
    throw error;
  }
};

// Record a cash transaction
export const recordCashTransaction = async (transaction: CashTransaction): Promise<CashTransaction> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/cash/transactions`, transaction, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error recording cash transaction:', error);
    throw error;
  }
};

// Get cash transaction history
export const getCashTransactions = async (startDate?: string, endDate?: string): Promise<CashTransaction[]> => {
  try {
    let url = `${API_BASE_URL}/cash/transactions`;
    
    // Add date filters if provided
    if (startDate || endDate) {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      url += `?${params.toString()}`;
    }
    
    const response = await axios.get(url, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching cash transactions:', error);
    throw error;
  }
};

// Update cash balance (manual reconciliation)
export interface UpdateBalanceRequest {
  safeBalance?: number;
  bankBalance?: number;
  notes?: string;
  date?: string;
}

export const updateBalance = async (data: UpdateBalanceRequest): Promise<CashBalance> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/cash/balance`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating cash balance:', error);
    throw error;
  }
};

// Get cash balance history
export const getBalanceHistory = async (startDate?: string, endDate?: string): Promise<CashBalance[]> => {
  try {
    let url = `${API_BASE_URL}/cash/balance/history`;
    
    // Add date filters if provided
    if (startDate || endDate) {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      url += `?${params.toString()}`;
    }
    
    const response = await axios.get(url, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching balance history:', error);
    throw error;
  }
}; 